import React, {
    useState,
    useRef,
    useEffect,
    useId,
    CSSProperties,
    MouseEvent,
} from "react";
import styled from "styled-components";
import { EditIcon } from "components/Icons";
import { Input } from "components/Input";
import { InputProps } from "components/Input/Input";

export interface EditableLabelProps extends Omit<
    InputProps,
    "onChange" | "ref" | "labelStyle" | "onClick" | "onCancel"
> {
    label: string;
    onSave: (newLabel: string) => void | Promise<void>;
    disabled?: boolean;
    placeholder?: string;
    inputSize?: "small" | "medium" | "large";
    labelClassName?: string;
    labelStyle?: CSSProperties;
    inputClassName?: string;
    "data-testid"?: string;
    isSelected?: boolean;
    isValid?: boolean;
    errorMessage?: string;
    onChange?: (newLabel: string) => void;
    onCancel?: () => void;
}

const EditableContainer = styled.div`
    display: flex;
    align-items: center;
    height: 30px;
    width: 100%;
    text-wrap: nowrap;
    text-overflow: ellipsis;
    gap: 8px;
`;

const LabelDisplay = styled.span<{ $isSelected: boolean; $disabled?: boolean }>`
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    font-size: 20px !important;
    font-weight: 400;
    color: ${({ $isSelected, theme }) =>
        !$isSelected
            ? theme.text.primary
            : theme.colors.background.secondary} !important;
    margin: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EditButton = styled.button<{ $isSelected: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;

    color: ${({ $isSelected, theme }) =>
        !$isSelected
            ? theme.text.primary
            : theme.colors.background.secondary} !important;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        opacity: 1;
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
`;

const InlineEditableInputContainer = styled.div`
    position: relative;
`;

const ErrorMessage = styled.span`
    position: absolute;
    display: block;
    font-size: 13px;
    line-height: 20px;
    font-weight: 500;
    color: ${({ theme }) => theme.danger};
    margin-top: 6px;
    z-index: 1;
    animation: slideIn 0.2s ease;

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(-2px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const InlineEditableInput = styled(Input)<{ $isSelected: boolean }>`
    padding: 0;

    & > div {
        margin-bottom: 0;
        width: auto;
        display: inline-block;
    }

    & > div > label {
        display: none;
    }

    & input {
        padding: 0;
        margin: 0;
        border-color: ${({ $isSelected, theme }) =>
            !$isSelected
                ? theme.text.primary
                : theme.colors.background.secondary};
        background: transparent;
        min-height: auto;
        font-weight: inherit;
        font-family: inherit;
        color: inherit;
        border-radius: 0;
        outline: none;
        box-shadow: none;
        width: auto;
        min-width: 100px;
        margin-bottom: 0;

        &:hover {
            border-color: ${({ $isSelected, theme }) =>
                !$isSelected
                    ? theme.text.primary
                    : theme.colors.background.secondary};
        }
        &:focus {
            border-color: ${({ $isSelected, theme }) =>
                !$isSelected
                    ? theme.text.primary
                    : theme.colors.background.secondary};
        }

        &::placeholder {
            color: ${({ theme }) => theme.text.secondary};
            opacity: 0.6;
        }
    }

    & > div > span:last-child {
        display: none;
    }
`;

export const EditableLabel: React.FC<EditableLabelProps> = ({
    label,
    onSave,
    disabled = false,
    placeholder = "Enter value",
    "data-testid": dataTestId,
    labelClassName,
    inputClassName,
    isSelected = true,
    labelStyle,
    errorMessage,
    isValid = true,
    onChange,
    onCancel,
    "aria-label": ariaLabel,
    ...props
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [value, setValue] = useState(label);
    const inputRef = useRef<HTMLInputElement>(null);
    // Sync guard for double Enter before re-render with isSaving.
    const isSavingRef = useRef(false);
    const errorMessageRef = useRef(errorMessage);
    errorMessageRef.current = errorMessage;
    const errorId = useId();
    const inputAriaLabel = ariaLabel ?? "Edit label";

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleEditClick = () => {
        if (!disabled) {
            setValue(label);
            setIsEditing(true);
        }
    };

    const handleSave = async () => {
        if (isSavingRef.current) {
            return;
        }

        if (!value.trim() || value === label) {
            handleCancel();

            return;
        }

        isSavingRef.current = true;
        setIsSaving(true);

        try {
            await onSave(value.trim());
            setIsEditing(false);
        } catch {
            // Keep editing; parent surfaces the failure via errorMessage.
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSavingRef.current) {
            return;
        }

        setValue(label);
        setIsEditing(false);

        if (!onCancel) {
            return;
        }

        onCancel();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            handleCancel();

            return;
        }

        if (!isValid || isSavingRef.current) {
            return;
        }

        if (event.key !== "Enter") {
            return;
        }

        handleSave();
    };

    const handleClick = (event: MouseEvent<HTMLInputElement>): void => {
        event.stopPropagation();
    };

    const handleBlur = () => {
        // Keep failed/pending save visible without stealing focus back from
        // other controls (e.g. selecting another account card).
        if (isSavingRef.current || errorMessageRef.current) {
            return;
        }

        handleCancel();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);

        if (!onChange) {
            return;
        }

        onChange(event.target.value);
    };

    const { style, className: propsInputClassname, ...otherProps } = props;

    const fullStyle: CSSProperties = {
        ...style,
        background: "transparent",
        padding: "0",
        border: "none",
        outline: "none",
        minHeight: "auto",
        height: "30px",
    };

    const currentErrorMessage: string | undefined = errorMessage;

    if (isEditing && !disabled) {
        return (
            <InlineEditableInputContainer
                onClick={(event) => event.stopPropagation()}
            >
                <InlineEditableInput
                    inputRef={inputRef}
                    value={value}
                    onClick={handleClick}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    data-testid={dataTestId ? `${dataTestId}-input` : undefined}
                    autoFocus
                    $isSelected={isSelected}
                    style={fullStyle}
                    wrapperStyle={{ marginBottom: "0" }}
                    className={`inline-editable-input ${inputClassName} ${propsInputClassname}`}
                    withoutHoverUI
                    disabled={isSaving}
                    {...otherProps}
                    aria-label={inputAriaLabel}
                    aria-invalid={currentErrorMessage ? true : undefined}
                    aria-describedby={
                        currentErrorMessage ? errorId : undefined
                    }
                />
                {!!currentErrorMessage && (
                    <ErrorMessage
                        id={errorId}
                        role="alert"
                        onMouseDown={(event) => {
                            // Prevent input blur→cancel when pressing the error text.
                            event.preventDefault();
                        }}
                    >
                        {currentErrorMessage}
                    </ErrorMessage>
                )}
            </InlineEditableInputContainer>
        );
    }

    return (
        <EditableContainer onClick={(event) => event.stopPropagation()}>
            <LabelDisplay
                $isSelected={isSelected}
                $disabled={disabled}
                className={`editable-label-text ${labelClassName || ""}`}
                onClick={(event) => {
                    event.stopPropagation();
                    handleEditClick();
                }}
                style={labelStyle}
                data-testid={dataTestId ? `${dataTestId}-label` : undefined}
            >
                {label || placeholder}
            </LabelDisplay>
            {!disabled && (
                <EditButton
                    title={`Edit ${label}`}
                    $isSelected={isSelected}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick();
                    }}
                    disabled={disabled}
                    aria-label={`Edit ${label}`}
                >
                    <EditIcon color="currentColor" size={14} />
                </EditButton>
            )}
        </EditableContainer>
    );
};
